import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    Button,
    DialogTitle,
    DialogContent,
    Typography,
    FormControl,
    InputLabel,
    FilledInput,
    CircularProgress,
    ListItem,
    ListItemIcon,
    List,
    ListItemText,
    Divider,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { GetSubscriptionSelectors } from '../../selectors/get-subscription-selectors';
import { useAuthHelper } from '../AuthContextProvider';
import { Thunks } from '../../actions';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { makeStyles } from '@mui/styles';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { GetPartNumberDetails } from '../../types/device';

type DialogProps = {
    show: boolean;
    closeDialog: Function;
    selectedOption: any;
    mappingValue: string
};
const useStyles = makeStyles({
    customMargin: {
        marginLeft: '20px',
        marginRight: '35px',
    }
});
export const ActivationIdDialog: React.FC<DialogProps> = (props) => {
    const { t } = useLanguageLocale();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const currentAppRoute = useCurrentAppRoute();
    const [activationId, setActivationId] = useState('');
    const address = {};
    const classes = useStyles();
    const mappingState = useSelector(GetSubscriptionSelectors.mappingSiteIdActivationId);
    const { addToast } = useToasts();
    const [value, setValue] = React.useState('');
    const [id, setId] = React.useState('');
    const [selectedID, setSelectedId] = React.useState('');
    const [siteId, setSiteId] = React.useState('');
    const [mappingHierarchy, setMappingHierarchy] = React.useState('');;
    const getPartNumberDetail = useSelector(GetSubscriptionSelectors.GetPartNumber);
    const [getPartNumberData, setGetPartNumberData] = useState<GetPartNumberDetails | null>(null);

    React.useEffect(() => {
        if (props.mappingValue !== undefined) {
            if (selectedID === '') {
                if (currentAppRoute.type === 'AcquireSubscription') {
                    if (props.mappingValue === 'Organization') {
                        setSelectedId(currentAppRoute.orgId);
                    } else if (props.mappingValue === 'Location') {
                        setSelectedId(currentAppRoute.locationId);
                    } else {
                        setSelectedId(currentAppRoute.siteId);
                    }
                }
            }
        }
    }, [props.mappingValue]);

    React.useEffect(() => {
        if (getPartNumberDetail.partNumberData) {
            setGetPartNumberData(getPartNumberDetail.partNumberData);
        }

    }, [getPartNumberDetail]);

    React.useEffect(() => {
        if (getPartNumberData) {
            //If not empty then extract some values from results
            if (getPartNumberData.results.length !== 0) {
                setMappingHierarchy(getPartNumberData.results[0].mappingHierarchy);
                setSiteId(getPartNumberData.siteId)
            } else {
                setMappingHierarchy('')
                setSiteId('')
            }
        }
    }, [getPartNumberData]);

    const onChangeActivationId = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setActivationId(e.target.value);
        if (!mappingState.mappingLoading) {
            // setIdValid(true); commenting for now as we are not using this
        }
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value);
        const targetValue = e.target.value;
        if (currentAppRoute.type === 'AcquireSubscription') {
            if (targetValue === 'Organization') {
                setId(currentAppRoute.orgId);
            } else if (targetValue === 'Location') {
                setId(currentAppRoute.locationId);
            } else {
                setId(currentAppRoute.siteId);
            }
        }
    };
    const submitActivationId = (): void => {
        const data = {
            activationId: activationId,
            address: address,
            siteId: id ? id : siteId
        };
        dispatch(Thunks.GetSubscription.MapSiteIdActivationId(data, authHelper, addToast));
    };
    const isButtonDisabled = (): boolean => {
        // Primary check on mappingHierarchy
        if (mappingHierarchy === '') {
            // Check if both id and activationId are blank
            if (id === '' || activationId === '') {
                return true;
            }
        } else {
            // Secondary check when mappingHierarchy is not blank
            // Check if activationId is blank
            if (activationId === '') {
                return true;
            }
        }
        return false;
    };

    const handleClose = (): void => {
        props.closeDialog();
    };
    return (
        <>
            {mappingState.errorCode || mappingState.mappingSuccess ? props.closeDialog() : <></>}
            {
                <Dialog
                    disableEscapeKeyDown={true}
                    open={props.show}
                    onClose={handleClose}
                    aria-labelledby="form-dialog-title"
                    maxWidth="sm"
                >
                    <DialogTitle className={classes.customMargin} id="form-dialog-title" style={{ marginTop: '10px' }}>
                        {props.selectedOption === 'ActivationId' && t('ACTIONS.ACTIVATE_ID')}
                    </DialogTitle>
                    <DialogContent className={classes.customMargin}>
                        <>
                            <div>
                                <Typography variant="body2" component="p" style={{ marginBottom: 30 }}>
                                    {props.selectedOption === 'ActivationId' && t('ACTIONS.ACTIVATE_ID_INFO')}
                                </Typography>
                                {props.selectedOption === 'ActivationId' && (
                                    <FormControl variant={'filled'} fullWidth style={{ marginBottom: 10 }}>
                                        <InputLabel id={'new-label'} htmlFor={'activation-id'}>
                                            Activation ID *
                                        </InputLabel>
                                        <FilledInput
                                            data-testid={'enter-activation-id'}
                                            type={'text'}
                                            value={activationId}
                                            fullWidth
                                            onChange={(e): void => onChangeActivationId(e)}
                                            style={{ marginBottom: 20 }}
                                        />
                                    </FormControl>)}

                                {(props.selectedOption === 'Trial' || (props.selectedOption === 'ActivationId' && mappingHierarchy === '')) && (
                                    <>
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            style={{ marginBottom: '5px', fontWeight: 'bold' }}
                                        >
                                            {'Select activation level'}
                                        </Typography>
                                        <Typography variant="body1" component="p" style={{ marginBottom: '5px' }}>
                                            <p>
                                                Subscription Activation ID linking to{' '}
                                                <strong>
                                                    Activation Level can be done just once and cannot be changed after
                                                    its done.
                                                </strong>
                                                Changing it will require the complete Organization to be deleted and
                                                recreated.
                                            </p>
                                        </Typography>

                                        <List>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Radio
                                                        edge="start"
                                                        checked={value === 'Organization'}
                                                        value="Organization"
                                                        onChange={handleChange}
                                                        name="radio-button-demo"
                                                        style={{ marginTop: -35 }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <>
                                                            <Typography
                                                                variant="body1"
                                                                style={{
                                                                    fontWeight: 'bold',
                                                                    textAlign: 'left',
                                                                }}
                                                            >
                                                                Organization
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    //     borderBottom: '1px solid',
                                                                    paddingBottom: '3px',
                                                                }}
                                                            >
                                                                This applies the subscription to every location and
                                                                corresponding sites within your organization.
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Radio
                                                        edge="start"
                                                        checked={value === 'Location'}
                                                        value="Location"
                                                        onChange={handleChange}
                                                        name="radio-button-demo"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <>
                                                            <Typography
                                                                variant="body1"
                                                                style={{
                                                                    fontWeight: 'bold',
                                                                    textAlign: 'left',
                                                                }}
                                                            >
                                                                Location
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{

                                                                    paddingBottom: '3px',
                                                                }}
                                                            >
                                                                This applies the subscription to all sites within a
                                                                specific location.
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Radio
                                                        edge="start"
                                                        checked={value === 'Site'}
                                                        value="Site"
                                                        onChange={handleChange}
                                                        name="radio-button-demo"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <>
                                                            <Typography
                                                                variant="body1"
                                                                style={{
                                                                    fontWeight: 'bold',
                                                                    textAlign: 'left',
                                                                }}
                                                            >
                                                                Site
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{

                                                                    paddingBottom: '3px',
                                                                }}
                                                            >
                                                                This only applies the subscription to a single site.
                                                            </Typography>
                                                        </>
                                                    }
                                                />
                                            </ListItem>
                                            <Divider />
                                        </List>

                                    </>
                                )}

                            </div>
                        </>
                    </DialogContent>
                    <Divider />
                    <DialogActions className={classes.customMargin} style={{ justifyContent: 'space-between' }}>
                        <Button id="btnBack" data-testid="back" variant="outlined" color="primary" onClick={handleClose}>
                            {t('ACTIONS.BACK')}
                        </Button>

                        <Button
                            id="btnSubmit"
                            data-testid="submit"
                            variant="contained"
                            color="primary"
                            disabled={isButtonDisabled()}
                            onClick={submitActivationId}
                            startIcon={
                                mappingState.mappingLoading ? (
                                    <CircularProgress size="1rem" style={{ color: 'white' }} />
                                ) : (
                                    <></>
                                )
                            }
                        >
                            {t('ACTIONS.SUBMIT')}
                        </Button>
                    </DialogActions>
                </Dialog >}
            <>
            </>
        </>

    );
};
